import { getAuth } from "firebase/auth";
import { collection as fbCollection, getDoc, doc as fbDoc, getFirestore as fbGetFirestore } from "firebase/firestore";
import { collection, doc, getDocs, getFirestore, updateDoc } from "firebase/firestore";
import { Download, Edit, Eye, FileDown, Receipt, Save, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useToast } from "../../components/ui/use-toast";
import { app } from "../../firebase";
import { FeeItem, updateFeeStatus } from "../../services/feeService";
import { record } from "zod";

const FeeList = ({}) => {
  const [userRole, setUserRole] = useState<string>("");
    // Fetch user role
    useEffect(() => {
      const fetchUserRole = async () => {
        try {
          const auth = getAuth(app);
          const db = fbGetFirestore(app);
          const user = auth.currentUser;
          if (user) {
            const userDoc = await getDoc(fbDoc(db, "users", user.uid));
            if (userDoc.exists()) {
              setUserRole(userDoc.data().role || "");
            }
          }
        } catch (e) {
          setUserRole("");
        }
      };
      fetchUserRole();
    }, []);
  // Form for payment record
  const form = useForm({
    defaultValues: {
      feeTotal: 0,
      feeDue: 0,
      feePaid: 0,
      status: 'pending',
      isNoDue: false
    },
  });

  // Handle form submission
  const onSubmit = async (data: any) => {
    if (!selectedFee) return;

    try {
      const feeTotal = Number(data.feeTotal);
      const feePaid = Number(data.feePaid);
      const feeDue = feeTotal - feePaid;

      // Determine new status
      let newStatus: 'paid' | 'pending' | 'overdue' | 'no_due' = selectedFee.newStatus;

      if (data.isNoDue) {
        newStatus = 'paid';
      } else if (feeTotal == feePaid) {
        newStatus = 'paid';
      } else if (feeTotal > 0) {
        newStatus = 'pending';
      }

      let body = {
        ...selectedFee,
        feeTotal: feeTotal,
        feePaid: feePaid,
        feeDue: feeDue,
        feeStatus: newStatus,
      }

      await updateDoc(doc(db, "students", selectedFee?.id), body);
      toast({
        title: "Fee updated",
        description: "Fee record has been updated successfully.",
      });
      setStudents(prevStudents =>
        prevStudents.map(student =>
          student.id === selectedFee.id
            ? {
              ...student,
              feeStatus: newStatus,
              feePaid: body?.feePaid,
              feeDue: body?.feeDue
            }
            : student
        )
      );
      setShowEditSheet(false);
    }
    catch (error) {
      console.error("Error updating fee record:", error);
      toast({
        title: "Error",
        description: "Failed to update fee record. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle edit fee record
  const handleEditFee = (fee: any) => {
    setSelectedFee(fee);
    form.reset({
      feeTotal: fee?.feeTotal || 0,
      feePaid: fee?.feePaid || 0,
      isNoDue: fee?.feeStatus === 'no_due',
    });
    setShowEditSheet(true);
  };

  // Handle view receipt
  const handleViewReceipt = (fee: any) => {
    setSelectedFee(fee);
    setShowReceiptDialog(true);
  };

  // Handle download receipt
  const handleDownloadReceipt = (fee: any) => {
    toast({
      title: "Receipt Downloaded",
      description: `Receipt for ${fee.studentsName} has been downloaded.`,
    });
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedFee, setSelectedFee] = useState<any | null>(null);
  const [feeRecords, setFeeRecords] = useState<FeeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const { toast } = useToast();
  const [students, setStudents] = useState([]); // State to hold student data 
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null); // Sorting state
  const db = getFirestore(app);
  const auth = getAuth(app);

  // const [filteredRecords, setFilteredRecords] = useState([]);

  const filteredRecords = students.filter((record) => {
    // Check if student matches search query  
    const matchesSearch =
      (record?.studentsName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record?.studentIdNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record?.courses?.some((course) =>
          course?.courseName?.toLowerCase().includes(searchQuery.toLowerCase())
        ));

    // Check if record matches active tab  
    const matchesStatus = activeTab === "all" || record?.feeStatus === activeTab;

    // Return true if both conditions are met  
    return (matchesSearch || searchQuery === "") && matchesStatus;
  });


  // Fetch fee records
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const studentsCollection = collection(db, "students");
        const studentSnapshot = await getDocs(studentsCollection);
        let studentList = studentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        studentList = studentList.filter((x: any) => x.feeTotal > '0').map((data: any) => {
          return {
            ...data,
            feeStatus: data?.feeDue > 0 ? 'pending' : 'paid'
          }
        });
        setStudents(studentList);
      } catch (error) {
        console.error("Error fetching students:", error);
        toast({
          title: "Error",
          description: "Failed to load student records. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, []);

  // Sorting logic
  const sortedRecords = (() => {
    let sortable = [...filteredRecords];
    if (sortConfig !== null) {
      sortable.sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';
        // For string comparison (case-insensitive)
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  })();

  // Handle sort click
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        // Toggle direction
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Render status badge
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-500">Paid</Badge>;
      case "pending":
        return <Badge variant="outline" className="text-amber-500 border-amber-500">Pending</Badge>;
      case "overdue":
        return <Badge variant="destructive">Overdue</Badge>;
      case "no_due":
        return <Badge variant="outline" className="text-blue-500 border-blue-500">No Due</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };


  //  const onTabChange =(value)=>{
  //   setActiveTab(value);
  //   setFilteredRecords(getFilteredRecords(value));

  //  }

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text -2xl font-bold">Fee List</h1>
        <Button variant="outline">
          <FileDown className="mr-2 h-4 w-4" />
          Export Fee Records
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="pb-2">Student Fee Records</CardTitle>
          <div className="flex flex-col sm:flex-row gap-4 mt-4">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search by name or ID..."
                className="pl-8 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue={activeTab} value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('studentIdNo')}
                    >
                      Student ID
                      {sortConfig?.key === 'studentIdNo' && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer select-none"
                      onClick={() => handleSort('studentsName')}
                    >
                      Name
                      {sortConfig?.key === 'studentsName' && (
                        <span className="ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </TableHead>
                    {/* <TableHead>Course</TableHead> */}
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Paid Amount</TableHead>
                    <TableHead>Due Amount</TableHead>
                    {/* <TableHead>Due Date</TableHead> */}
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10">
                        Loading fee records...
                      </TableCell>
                    </TableRow>
                  ) : sortedRecords.length > 0 ? (
                    sortedRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{record.studentIdNo}</TableCell>
                        <TableCell>{record?.studentsName}</TableCell>
                        {/* <TableCell>{record?.courses[0]?.courseName}</TableCell> */}
                        <TableCell>₹{(record?.feeTotal || 0)?.toLocaleString()}</TableCell>
                        <TableCell>₹{record?.feePaid?.toLocaleString()}</TableCell>
                        <TableCell>₹{record?.feeDue?.toLocaleString()}</TableCell>
                        {/* <TableCell>{new Date(record?.dueDate).toLocaleDateString()}</TableCell> */}
                        <TableCell>{renderStatusBadge(record?.feeStatus)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {userRole === "MASTERADMIN" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditFee(record)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                            )}
                            {(record.feeStatus === "paid" || record.feeTotal) && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewReceipt(record)}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadReceipt(record)}
                                >
                                  <Download className="h-4 w-4 mr-1" />
                                  Download
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-10">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <Search className="h-10 w-10 mb-2" />
                          <p className="text-lg">No fee records found</p>
                          <p className="text-sm">Try adjusting your search or filter</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Edit Fee Sheet */}
      <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
        <SheetContent className="sm:max-w-md sm:max-w-md h-[99vh] overflow-auto">
          <SheetHeader>
            <SheetTitle>Edit Fee Record</SheetTitle>
          </SheetHeader>

          {selectedFee && (
            <div className="space-y-4 mt-2">
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <h3 className="font-medium">Student Information</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Student ID:</span>
                      <p>{selectedFee.studentIdNo}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Name:</span>
                      <p>{selectedFee.studentsName}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Course:</span>
                      <p>{selectedFee.courses[0]?.courseName}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex h-5 items-center">
                          <input
                            type="checkbox"
                            id="isNoDue"
                            className="h-4 w-4 rounded border-gray-300"
                            {...form.register("isNoDue")}
                            onChange={(e) => {
                              form.setValue("isNoDue", e.target.checked);
                              form.setValue('feePaid', form.getValues('feeTotal'));
                              // if (e.target.checked) {
                              //   form.setValue("feeTotal", 0);
                              // }
                            }}
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor="isNoDue" className="font-medium text-gray-700">
                            Mark as No Due
                          </label>
                        </div>
                      </div>

                      {(
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="amount">Total Fee Amount</Label>
                            <Input id="amount" value={`₹${selectedFee.feeTotal?.toLocaleString()}`} disabled />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="feePaid">Paid Amount</Label>
                            <Input
                              id="feePaid"
                              type="number"
                              step="0.01"
                              {...form.register("feePaid", {
                                valueAsNumber: true,
                                validate: (value) => value >= 0 || "Amount cannot be negative"
                              })}
                            />
                            {form.formState.errors.feeTotal && (
                              <p className="text-sm text-red-500">{form.formState.errors.feeTotal.message}</p>
                            )}
                          </div>
                        </>
                      )}

                      <div className="pt-4 flex justify-end space-x-2">
                        <SheetClose asChild>
                          <Button type="button" variant="outline">
                            Cancel
                          </Button>
                        </SheetClose>
                        <Button type="submit">
                          <Save className="mr-2 h-4 w-4" />
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Fee Receipt</DialogTitle>
            <DialogDescription>
              Receipt details for {selectedFee?.studentsName}
            </DialogDescription>
          </DialogHeader>

          {selectedFee && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Receipt className="h-8 w-8 text-green-600" />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => handleDownloadReceipt(selectedFee)}>
                  <Download className="mr-2 h-4 w-4" />
                  Download Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default FeeList;
